import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/api/client";

export const BROWSER_ALERTS_KEY = "lc_visitor_chat_browser_alerts";
const POLL_MS = 20000;
const HEARTBEAT_MS = 30000;
const DEDUP_MS = 10 * 60 * 1000;
const OPERATOR_ROLES = ["sales_manager", "admin", "super_admin"];

const VisitorAlertsContext = createContext({
  unread: 0, browserEnabled: false, browserSupported: false, enableBrowser: async () => false, refresh: () => {}, youOnline: false,
});
export const useVisitorAlerts = () => useContext(VisitorAlertsContext);

const supported = () => typeof window !== "undefined" && "Notification" in window;
const enabledNow = () => supported() && window.Notification.permission === "granted"
  && window.localStorage.getItem(BROWSER_ALERTS_KEY) === "on";

export function VisitorAlertsProvider({ role, children }) {
  const active = OPERATOR_ROLES.includes(role);
  const [unread, setUnread] = useState(0);
  const [browserEnabled, setBrowserEnabled] = useState(enabledNow());
  const [youOnline, setYouOnline] = useState(false);
  const seen = useRef(null);
  const lastByConversation = useRef({});

  const refresh = useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await api.get("/visitor-chat/unread-summary");
      setUnread(data.unread_conversations || 0);
      const alerts = data.alerts || [];
      if (seen.current === null) { seen.current = new Set(alerts.map(a => a.id)); return; }
      for (const a of alerts) {
        if (seen.current.has(a.id)) continue;
        seen.current.add(a.id);
        const last = lastByConversation.current[a.conversation_id] || 0;
        if (!enabledNow() || Date.now() - last < DEDUP_MS) continue;
        lastByConversation.current[a.conversation_id] = Date.now();
        new window.Notification(a.title || "New website chat", { body: a.body, tag: `visitor-chat-${a.conversation_id}` });
      }
    } catch { /* badge keeps last value */ }
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [active, refresh]);

  useEffect(() => {
    if (!active) return undefined;
    let timer = null;
    const beat = async () => {
      try { await api.post("/visitor-team/heartbeat"); setYouOnline(true); } catch { setYouOnline(false); }
    };
    const start = () => { if (timer) return; beat(); timer = setInterval(beat, HEARTBEAT_MS); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } setYouOnline(false); };
    const onVisibility = () => (document.visibilityState === "hidden" ? stop() : start());
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { document.removeEventListener("visibilitychange", onVisibility); stop(); };
  }, [active]);

  const enableBrowser = async () => {
    if (!supported()) return false;
    const permission = await window.Notification.requestPermission();
    if (permission === "granted") window.localStorage.setItem(BROWSER_ALERTS_KEY, "on");
    setBrowserEnabled(enabledNow());
    return permission === "granted";
  };

  return (
    <VisitorAlertsContext.Provider value={{ unread, browserEnabled, browserSupported: supported(), enableBrowser, refresh, youOnline }}>
      {children}
    </VisitorAlertsContext.Provider>
  );
}

export function UnreadBadge({ count, testid }) {
  if (!count) return null;
  return (
    <span data-testid={testid} aria-label={`${count} unread chats`}
      className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-rose-600 px-1.5 text-[10px] font-bold leading-5 text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
}
